import type { CheckItem, DatabaseFile, Section } from '../../types';
import { migrateProseToItems } from '../../services/MarkdownService';
import { audioDisplayLabel, audioRepoPath, mimeForAudioPath } from '../../utils/itemAudio';
import { isAudioAttachmentFilename } from '../../utils/itemMediaPlayback';
import { mimeForVideoPath, videoRepoPath } from '../../utils/itemVideo';
import { applyVideoFilenameEdit } from '../../utils/videoFilenameEdit';
import {
  createVideoThumbnailFromFile,
  isMostlyBlackImageElement,
  videoThumbLoadRepoPaths,
  videoThumbnailRepoPath,
  waitForImageLoad,
} from '../../utils/videoThumbnail';
import { imageRepoPath } from '../../utils/itemImages';
import { withoutGitBusyOverlay } from '../../utils/gitBusy';
import { folderLabel, parentDirOfMarkdown } from '../../utils/markdownPaths';
import { appAlert, appChoose, appConfirm, appPrompt } from '../../utils/appDialog';
import { sortSectionsByTitle } from '../../utils/sortPickerChoices';
import {
  copyTextToClipboard,
  formatItemLine,
  formatSectionListForClipboard,
} from '../../utils/formatListClipboard';
import { displayItemIndices, sectionItemSortMenuLabel, toggleSectionItemSort } from '../../utils/sectionItemSort';
import { generateAndCopyShareLink } from '../../utils/shareLinkFlow';
import {
  sharePlaintextForItem,
  sharePlaintextForSection,
} from '../../utils/sharePayload';
import { openItemTextEditor } from '../../utils/itemTextModal';
import {
  itemComposerDraftKey,
  recordUnsavedItemEdit,
} from '../../utils/itemEditorDraft';
import { queueOrphanedAudioForItem } from '../../services/ItemAudioSync';
import { queueOrphanedVideoForItem } from '../../services/ItemVideoSync';
import { queueOrphanedImagesForItem } from '../../services/ItemImageSync';
import { emptyCheckItemMedia } from '../../types';
import type { FileViewDeps } from './FileViewDeps';
import { flashCopyOnButton } from '../../utils/copyFeedback';
import { escapeAttr, escapeHtml } from '../../utils/escapeHtml';
import { dragDistanceExceeded, getDragAfterElement } from '../../utils/pointerDragReorder';
import {
  TODAY,
  collectPendingAudioFromInput,
  collectPendingFromInput,
  collectAudioFromVideoInput,
  collectPendingVideoFromInput,
  ensureItemMediaFields,
  fileHasUnsavedMediaChanges,
  itemTextTooltipAttr,
  renderItemTextHtml,
} from './fileViewHelpers';
import { setTranslateSessionActive } from '../../translate/translateApi';
import { hideTranslateBubble } from '../../translate/translateBubble';
import { isSpanishTranslatePage } from '../../translate/translatePage';
import { formatSpanishGuillemetsForDisplay } from '../../utils/spanishDisplayQuotes';
import {
  findSpanishReviewSectionIndex,
  formatSpanishReviewItemText,
  setSpanishReviewHandler,
  spanishFromReviewItemText,
} from '../../translate/spanishReview';
import {
  getLastMediaProbe,
  isMediaDebugEnabled,
  logMediaDebug,
  logThumbDebug,
} from '../../utils/mediaDebugLog';
import {
  firefoxRejectsCrossOriginVideoSrc,
  isFirefoxBrowser,
  prefersFirefoxDirectVideoSrc,
  prefersVideoHttpsStreamFirst,
  prefersVideoStreamPlayback,
  videoPreloadForPlayback,
} from '../../utils/videoPlaybackMode';

const PROXY_PLAYBACK_TIMEOUT_MS = 8000;
/** Mutable gesture / blob state owned by FileView, shared with sections UI. */
export interface FileViewSectionsState {
  pendingBlobUrls: string[];
  itemGestureAbort: AbortController | null;
  sectionDragGestureAbort: AbortController | null;
  sectionsPointerAbort: AbortController | null;
  /** Bumped on each renderSections; hydrators ignore stale async completions. */
  mediaHydrateGeneration: number;
  /** Latest render target for delegated media file-input handlers. */
  activeContainer: HTMLElement | null;
  activeFile: DatabaseFile | null;
  activeOnDirty: (() => void) | null;
}

export class FileViewSections {
  constructor(
    private readonly deps: FileViewDeps,
    private readonly state: FileViewSectionsState
  ) {}

  private get onMoveItemToNew() {
    return this.deps.onMoveItemToNew;
  }
  private get onMoveItemToSection() {
    return this.deps.onMoveItemToSection;
  }
  private get onMoveAllItemsToSection() {
    return this.deps.onMoveAllItemsToSection;
  }
  private get onMoveSectionToNew() {
    return this.deps.onMoveSectionToNew;
  }
  private get onMoveSectionToPage() {
    return this.deps.onMoveSectionToPage;
  }
  private get listPageSections() {
    return this.deps.listPageSections;
  }
  private get listOtherPages() {
    return this.deps.listOtherPages;
  }
  private get resolveImageUrl() {
    return this.deps.resolveImageUrl;
  }

  private get resolveAudioUrl() {
    return this.deps.resolveAudioUrl;
  }

  private get resolveVideoUrl() {
    return this.deps.resolveVideoUrl;
  }

  renderSections(container: HTMLElement, file: DatabaseFile, onDirty: () => void): void {
    this.state.activeContainer = container;
    this.state.activeFile = file;
    this.state.activeOnDirty = onDirty;
    for (const section of file.sections) {
      for (const item of section.items) ensureItemMediaFields(item);
    }

    const translateOnPage = isSpanishTranslatePage(file.path);
    setTranslateSessionActive(translateOnPage);
    if (translateOnPage) {
      setSpanishReviewHandler(({ spanish, sourceSentence }) => {
        this.addSpanishReviewItem(container, file, onDirty, spanish, sourceSentence);
      });
    } else {
      setSpanishReviewHandler(null);
      hideTranslateBubble();
    }

    migrateProseToItems(file.sections);
    for (const url of this.state.pendingBlobUrls) URL.revokeObjectURL(url);
    this.state.pendingBlobUrls = [];
    this.state.itemGestureAbort?.abort();
    this.state.itemGestureAbort = null;
    this.state.sectionDragGestureAbort?.abort();
    this.state.sectionDragGestureAbort = null;
    this.state.sectionsPointerAbort?.abort();
    this.state.sectionsPointerAbort = new AbortController();
    const sectionsPointerSignal = this.state.sectionsPointerAbort.signal;

    const sectionsEl = container.querySelector<HTMLElement>('#sections-container')!;
    const hydrateGen = this.state.mediaHydrateGeneration + 1;
    this.state.mediaHydrateGeneration = hydrateGen;
    sectionsEl.innerHTML = file.sections.map((section, si) => `
      <div class="section-block" data-section="${si}">
        <button type="button" class="section-drag-handle" aria-label="Drag to reorder section" title="Drag to reorder section">☰</button>
        <details class="section" open>
        <summary class="section-title">
          <span class="section-title-text">${escapeHtml(section.title)}</span>
          ${this.sectionBadge(section)}
          <span class="section-title-actions">
            <button type="button" class="btn-edit-section btn-icon" data-section="${si}" title="Edit section title">✏️</button>
            <button type="button" class="btn-delete-section btn-icon btn-icon-danger" data-section="${si}" title="Delete section">🗑</button>
            ${this.renderSectionMoreMenu(si, section)}
          </span>
        </summary>
        <div class="section-body">
          ${section.prose.length > 0
            ? `<div class="prose">${section.prose.map(l => `<p>${escapeHtml(l)}</p>`).join('')}</div>`
            : ''}
          <div class="add-item-row">
            <button type="button" class="btn-add-item" data-section="${si}">＋ Add item</button>
          </div>
          <ul class="item-list" data-section="${si}">
            ${displayItemIndices(section).map(ii => this.renderItem(file, section.items[ii]!, si, ii)).join('')}
          </ul>
        </div>
      </details>
      </div>
    `).join('');

    // Delete section buttons
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-delete-section').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation(); // don't toggle <details>
        const si = parseInt(btn.dataset['section']!);
        const section = file.sections[si]!;
        void (async () => {
        const confirmed = await appConfirm(
          `All ${section.items.length} item(s) in this section will be removed.`,
          {
            title: `Delete section "${section.title}"?`,
            confirmLabel: 'Delete',
            danger: true,
          }
        );
        if (!confirmed) return;
        file.sections.splice(si, 1);
        this.renderSections(container, file, onDirty);
        onDirty();
        })();
      });
    });

    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-share-section').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const section = file.sections[si];
        if (!section) return;
        void generateAndCopyShareLink(sharePlaintextForSection(section));
      });
    });

    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-share-item').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const ii = parseInt(btn.dataset['item']!, 10);
        const item = file.sections[si]?.items[ii];
        if (!item) return;
        void generateAndCopyShareLink(sharePlaintextForItem(item));
      });
    });

    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-copy-item').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const ii = parseInt(btn.dataset['item']!, 10);
        const item = file.sections[si]?.items[ii];
        if (!item) return;
        const text = formatItemLine(item);
        void (async () => {
          try {
            await copyTextToClipboard(text);
            const moreBtn = sectionsEl.querySelector<HTMLButtonElement>(
              `.btn-item-more[data-section="${si}"][data-item="${ii}"]`
            );
            if (moreBtn) flashCopyOnButton(moreBtn);
          } catch (err) {
            await appAlert(
              err instanceof Error ? err.message : 'Could not copy to clipboard.',
              'Copy failed'
            );
          }
        })();
      });
    });

    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-toggle-item-sort').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const section = file.sections[si];
        if (!section) return;
        toggleSectionItemSort(section);
        this.renderSections(container, file, onDirty);
        onDirty();
      });
    });

    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-copy-section').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const section = file.sections[si];
        if (!section) return;
        const text = formatSectionListForClipboard(section);
        void (async () => {
          try {
            await copyTextToClipboard(text);
            const moreBtn = sectionsEl.querySelector<HTMLButtonElement>(
              `.btn-section-more[data-section="${si}"]`
            );
            if (moreBtn) flashCopyOnButton(moreBtn);
          } catch (err) {
            await appAlert(
              err instanceof Error ? err.message : 'Could not copy to clipboard.',
              'Copy failed'
            );
          }
        })();
      });
    });

    // Delete item buttons
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-delete-item').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const si  = parseInt(btn.dataset['section']!);
        const ii  = parseInt(btn.dataset['item']!);
        const removed = file.sections[si]!.items[ii]!;
        queueOrphanedImagesForItem(file, removed);
        queueOrphanedAudioForItem(file, removed);
        queueOrphanedVideoForItem(file, removed);
        file.sections[si]!.items.splice(ii, 1);
        this.renderSections(container, file, onDirty);
        onDirty();
      });
    });

    this.wireSectionPointerReorder(container, sectionsEl, file, onDirty, sectionsPointerSignal);
    this.wireItemListPointerReorder(container, sectionsEl, file, onDirty);
    this.wireItemTextClick(container, sectionsEl, file, onDirty);
    this.wireSectionTitleEdit(container, sectionsEl, file, onDirty);
    this.wireMoreMenus(sectionsEl);
    if (this.onMoveItemToNew && this.onMoveItemToSection && this.listPageSections) {
      this.wireMoveItem(container, file, sectionsEl);
    }
    if (this.onMoveAllItemsToSection && this.listPageSections) {
      this.wireMoveAllSectionItems(container, file, sectionsEl);
    }
    if (this.onMoveSectionToNew && this.onMoveSectionToPage && this.listOtherPages) {
      this.wireMoveSection(container, file, sectionsEl);
    }
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-add-item').forEach(btn => {
      btn.addEventListener('click', () => {
        const si = parseInt(btn.dataset['section']!, 10);
        this.openNewItemEditor(container, sectionsEl, file, si, onDirty);
      });
    });

    this.wireItemPhotoPickers(container, sectionsEl, file, onDirty);
    this.wireItemMediaMenuPickers(container, sectionsEl, file, onDirty);
    this.wireItemImageActions(container, sectionsEl, file, onDirty);
    this.wireItemAudioActions(container, sectionsEl, file, onDirty);
    this.wireItemVideoActions(container, sectionsEl, file, onDirty);
    void this.hydrateItemImages(container, hydrateGen);
    void this.hydrateItemAudio(container, hydrateGen);
    this.wireItemVideoLazyPlay(container, hydrateGen);
    void this.hydrateItemVideoThumbs(container, hydrateGen);

    if (fileHasUnsavedMediaChanges(file)) onDirty();
  }

  wireSectionPointerReorder(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void,
    parentSignal: AbortSignal
  ): void {
    sectionsEl.addEventListener(
      'pointerdown',
      e => {
        if (e.button !== 0) return;
        if (!(e.target as HTMLElement).closest('.section-drag-handle')) return;
        e.stopPropagation();
        const block = (e.target as HTMLElement | null)?.closest('.section-block') as HTMLElement | null;
        if (!block || !sectionsEl.contains(block)) return;

        this.state.itemGestureAbort?.abort();
        this.state.itemGestureAbort = null;
        this.state.sectionDragGestureAbort?.abort();
        this.state.sectionDragGestureAbort = new AbortController();
        const g = this.state.sectionDragGestureAbort.signal;

        const pid = e.pointerId;
        const el = block;
        const startX = e.clientX;
        const startY = e.clientY;
        let active = false;

        const finish = (ev: PointerEvent) => {
          if (ev.pointerId !== pid) return;
          if (active) {
            try {
              el.releasePointerCapture(pid);
            } catch {
              /* ignore */
            }
            sectionsEl.classList.remove('section-reorder-active');
            el.classList.remove('section-dragging');
            const blocks = [...sectionsEl.querySelectorAll<HTMLElement>(':scope > .section-block')];
            const newOrder = blocks.map(b => file.sections[parseInt(b.dataset['section']!, 10)]!);
            const changed =
              newOrder.length !== file.sections.length || newOrder.some((s, i) => s !== file.sections[i]);
            if (changed) {
              file.sections.splice(0, file.sections.length, ...newOrder);
              this.renderSections(container, file, onDirty);
              onDirty();
            }
          }
          this.state.sectionDragGestureAbort?.abort();
          this.state.sectionDragGestureAbort = null;
        };

        const onPointerMove = (ev: PointerEvent) => {
          if (ev.pointerId !== pid) return;
          if (!active) {
            if (!dragDistanceExceeded(startX, startY, ev.clientX, ev.clientY)) return;
            active = true;
            sectionsEl.classList.add('section-reorder-active');
            el.classList.add('section-dragging');
            try {
              el.setPointerCapture(pid);
            } catch {
              /* ignore */
            }
          }
          ev.preventDefault();
          const afterEl = getDragAfterElement<HTMLElement>(
            sectionsEl,
            ev.clientY,
            ':scope > .section-block:not(.section-dragging)'
          );
          if (afterEl == null) sectionsEl.appendChild(el);
          else sectionsEl.insertBefore(el, afterEl);
        };

        window.addEventListener('pointermove', onPointerMove, { signal: g, passive: false });
        window.addEventListener('pointerup', finish, { signal: g });
        window.addEventListener('pointercancel', finish, { signal: g });
      },
      { signal: parentSignal }
    );
  }

  renderSectionMoreMenu(si: number, section: Section): string {
    const items: string[] = [
      `<button type="button" class="section-more-item btn-toggle-item-sort" data-section="${si}" role="menuitem">${sectionItemSortMenuLabel(section)}</button>`,
      `<button type="button" class="section-more-item btn-share-section" data-section="${si}" role="menuitem">🔗 Generate link</button>`,
      `<button type="button" class="section-more-item btn-copy-section" data-section="${si}" role="menuitem">📋 Copy</button>`,
    ];
    if (this.onMoveSectionToNew && this.onMoveSectionToPage && this.listOtherPages) {
      items.push(
        `<button type="button" class="section-more-item btn-move-section" data-section="${si}" role="menuitem">⇄ Move to page</button>`
      );
    }
    if (this.onMoveAllItemsToSection && section.items.length > 0) {
      items.push(
        `<button type="button" class="section-more-item btn-move-all-items" data-section="${si}" role="menuitem">⇄ Move all items to section</button>`
      );
    }
    return `
      <span class="section-more-menu" data-section="${si}">
        <button type="button" class="btn-section-more btn-icon" data-section="${si}" title="More actions" aria-label="More section actions" aria-haspopup="menu" aria-expanded="false">⋮</button>
        <div class="section-more-popover hidden" role="menu" data-section="${si}">
          ${items.join('')}
        </div>
      </span>`;
  }

  renderItemMoreMenu(si: number, ii: number, _section: Section): string {
    const items: string[] = [
      `<button type="button" class="section-more-item btn-menu-add-video" data-section="${si}" data-item="${ii}" role="menuitem">🎬 Add video</button>`,
      `<button type="button" class="section-more-item btn-menu-add-audio" data-section="${si}" data-item="${ii}" role="menuitem">🎤 Add audio</button>`,
      `<button type="button" class="section-more-item btn-copy-item" data-section="${si}" data-item="${ii}" role="menuitem">📋 Copy</button>`,
      `<button type="button" class="section-more-item btn-share-item" data-section="${si}" data-item="${ii}" role="menuitem">🔗 Generate link</button>`,
    ];
    if (this.onMoveItemToNew && this.onMoveItemToSection) {
      items.push(
        `<button type="button" class="section-more-item btn-move-item" data-section="${si}" data-item="${ii}" role="menuitem">⇄ Move to section</button>`
      );
    }
    return `
      <span class="item-more-menu" data-section="${si}" data-item="${ii}">
        <button type="button" class="btn-item-more btn-icon" data-section="${si}" data-item="${ii}" title="More actions" aria-label="More item actions" aria-haspopup="menu" aria-expanded="false">⋮</button>
        <div class="item-more-popover section-more-popover hidden" role="menu" data-section="${si}" data-item="${ii}">
          ${items.join('')}
        </div>
        <input type="file" class="item-audio-input" accept="audio/*" multiple tabindex="-1" aria-hidden="true" data-section="${si}" data-item="${ii}" />
        <input type="file" class="item-video-input" accept="video/*,audio/*" multiple tabindex="-1" aria-hidden="true" data-section="${si}" data-item="${ii}" />
      </span>`;
  }

  closeAllMoreMenus(sectionsEl: HTMLElement): void {
    sectionsEl.querySelectorAll<HTMLElement>('.section-more-popover, .item-more-popover').forEach(pop => {
      pop.classList.add('hidden');
    });
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-section-more, .btn-item-more').forEach(btn => {
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  wireMoreMenus(sectionsEl: HTMLElement): void {
    const close = () => this.closeAllMoreMenus(sectionsEl);

    const toggleMenu = (btn: HTMLButtonElement) => {
      const menu = btn.closest('.section-more-menu, .item-more-menu');
      const pop = menu?.querySelector<HTMLElement>('.section-more-popover, .item-more-popover');
      if (!pop) return;
      const open = pop.classList.contains('hidden');
      close();
      if (open) {
        pop.classList.remove('hidden');
        btn.setAttribute('aria-expanded', 'true');
      }
    };

    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-section-more, .btn-item-more').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        toggleMenu(btn);
      });
    });

    if (!sectionsEl.dataset['moreMenuDismiss']) {
      sectionsEl.dataset['moreMenuDismiss'] = '1';
      document.addEventListener('click', ev => {
        if (!(ev.target as HTMLElement).closest('.section-more-menu, .item-more-menu')) close();
      });
      document.addEventListener('keydown', ev => {
        if (ev.key === 'Escape') close();
      });
    }
  }

  wireSectionTitleEdit(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-edit-section').forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        const si = parseInt(btn.dataset['section']!, 10);
        const section = file.sections[si]!;
        const details = btn.closest('details.section') as HTMLDetailsElement | null;
        if (details) details.open = true;

        openItemTextEditor({
          value: section.title,
          title: 'Section title',
          placeholder: 'Section title',
          singleLine: true,
          translateEnabled: isSpanishTranslatePage(file.path),
          onDone: next => {
            if (!next) {
              void appAlert('Section title cannot be empty.');
              return;
            }
            section.title = next;
            this.renderSections(container, file, onDirty);
            onDirty();
          },
        });
      });
    });
  }

  ingestPhotosForItem(item: CheckItem, input: HTMLInputElement): boolean {
    const added = collectPendingFromInput(input);
    if (added.length === 0) return false;
    item.pendingImages.push(...added);
    input.value = '';
    return true;
  }

  ingestAudioForItem(item: CheckItem, input: HTMLInputElement): boolean {
    const added = collectPendingAudioFromInput(input);
    if (added.length === 0) return false;
    item.pendingAudio.push(...added);
    input.value = '';
    return true;
  }

  async ingestVideoForItem(
    item: CheckItem,
    input: HTMLInputElement
  ): Promise<{ ok: true; filenames: string[] } | { ok: false }> {
    const videos = collectPendingVideoFromInput(input, { lenient: true });
    const audios = collectAudioFromVideoInput(input);
    if (videos.length === 0 && audios.length === 0) return { ok: false };
    item.pendingVideo.push(...videos);
    item.pendingAudio.push(...audios);
    input.value = '';
    return { ok: true, filenames: videos.map(v => v.filename) };
  }

  /** Capture pending clip thumbs in-gallery (Firefox/mobile uses ffmpeg.wasm). */
  async capturePendingVideoThumbs(
    container: HTMLElement,
    item: CheckItem,
    onlyFilenames?: readonly string[]
  ): Promise<string[]> {
    const failed: string[] = [];
    const targets = onlyFilenames
      ? item.pendingVideo.filter(p => onlyFilenames.includes(p.filename))
      : item.pendingVideo;

    for (const pending of targets) {
      if (pending.thumbBlob) continue;

      const tile = container.querySelector<HTMLElement>(
        `.item-video-tile-pending[data-filename="${CSS.escape(pending.filename)}"]`
      );
      const mount = tile?.querySelector<HTMLElement>('.item-video-thumb-play-btn');
      const debugPath = `pending:${pending.filename}`;

      if (!mount) {
        logThumbDebug(debugPath, 'thumb-ingest-no-tile', 'Pending tile not in DOM yet', false);
        failed.push(pending.filename);
        continue;
      }

      logThumbDebug(debugPath, 'thumb-ingest-start', 'Capture after attach');
      tile?.classList.add('item-video-thumb-loading');

      try {
        const thumb = await createVideoThumbnailFromFile(pending.file, 0.5, 400, debugPath);
        pending.thumbBlob = thumb;
        pending.thumbPreviewUrl = URL.createObjectURL(thumb);
        this.state.pendingBlobUrls.push(pending.thumbPreviewUrl);
        const img = mount.querySelector<HTMLImageElement>('.item-video-thumb-img');
        if (img) {
          img.src = pending.thumbPreviewUrl;
          img.classList.remove('hidden');
        }
        logThumbDebug(debugPath, 'thumb-ingest-ok', `Preview ready (${thumb.size} bytes)`);
      } catch (err) {
        logThumbDebug(
          debugPath,
          'thumb-ingest-fail',
          err instanceof Error ? err.message : 'tile capture failed',
          false
        );
        failed.push(pending.filename);
      } finally {
        tile?.classList.remove('item-video-thumb-loading');
      }
    }

    if (failed.length > 0) {
      item.pendingVideo = item.pendingVideo.filter(p => !failed.includes(p.filename));
    }
    return failed;
  }

  openNewItemEditor(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    si: number,
    onDirty: () => void
  ): void {
    const draftKey = itemComposerDraftKey(file.path, si, 'new');
    const open = () => {
      openItemTextEditor({
        value: '',
        title: 'New item',
        placeholder: 'What do you want to remember?',
        checklistDate: TODAY,
        translateEnabled: isSpanishTranslatePage(file.path),
        composerDraftKey: draftKey,
        onDone: (value, date) => {
          const text = value.replace(/\r\n/g, '\n').trim();
          if (!text) {
            void appAlert('Item text cannot be empty.').then(() => open());
            return;
          }
          const itemDate = date || TODAY;
          file.sections[si]!.items.unshift({
            raw: '',
            text,
            date: itemDate,
            state: 'open',
            ...emptyCheckItemMedia(),
          });
          recordUnsavedItemEdit(file.path, {
            kind: 'new',
            sectionIndex: si,
            text,
            date: itemDate,
          });
          this.renderSections(container, file, onDirty);
          onDirty();
        },
      });
    };
    open();
  }

  openExistingItemEditor(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    si: number,
    ii: number,
    onDirty: () => void
  ): void {
    const item = file.sections[si]?.items[ii];
    if (!item) return;
    const draftKey = itemComposerDraftKey(file.path, si, 'existing', ii);
    const formatQuotes = isSpanishTranslatePage(file.path);
    const open = () => {
      openItemTextEditor({
        value: formatQuotes ? formatSpanishGuillemetsForDisplay(item.text) : item.text,
        title: 'Edit item',
        placeholder: 'Item text…',
        checklistDate: item.date || TODAY,
        translateEnabled: isSpanishTranslatePage(file.path),
        composerDraftKey: draftKey,
        onDone: (value, date) => {
          const text = value.replace(/\r\n/g, '\n').trim();
          if (!text) {
            void appAlert('Item text cannot be empty.').then(() => open());
            return;
          }
          const itemDate = date || TODAY;
          item.text = text;
          item.date = itemDate;
          item.raw = '';
          recordUnsavedItemEdit(file.path, {
            kind: 'edit',
            sectionIndex: si,
            itemIndex: ii,
            text,
            date: itemDate,
          });
          this.renderSections(container, file, onDirty);
          onDirty();
        },
      });
    };
    open();
  }

  private ensureSpanishReviewSection(file: DatabaseFile): number {
    let si = findSpanishReviewSectionIndex(file.sections);
    if (si >= 0) return si;
    file.sections.unshift({
      title: 'Review',
      level: 2,
      items: [],
      prose: [],
    });
    return 0;
  }

  private addSpanishReviewItem(
    container: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void,
    spanish: string,
    sourceSentence: string
  ): void {
    const si = this.ensureSpanishReviewSection(file);
    const items = file.sections[si]!.items;
    const text = formatSpanishReviewItemText(spanish, sourceSentence);
    const existing = items.findIndex(it => spanishFromReviewItemText(it.text) === spanish);
    if (existing >= 0) {
      if (existing > 0) {
        const [row] = items.splice(existing, 1);
        row!.text = text;
        items.unshift(row!);
      } else {
        items[0]!.text = text;
      }
    } else {
      items.unshift({
        raw: '',
        text,
        date: TODAY,
        state: 'open',
        ...emptyCheckItemMedia(),
      });
    }
    this.renderSections(container, file, onDirty);
    onDirty();
  }

  /** Tap item text to edit (e.g. recipe ingredients). */

  wireItemTextClick(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    const coarsePointer = window.matchMedia('(hover: none) and (pointer: coarse)').matches;

    sectionsEl.querySelectorAll<HTMLLIElement>('li.item-row').forEach(li => {
      const textEl = li.querySelector<HTMLElement>('.item-text');
      if (!textEl) return;
      textEl.classList.add('item-text-tappable');
      let pointerId = -1;
      let startX = 0;
      let startY = 0;
      let pointerDragged = false;

      textEl.addEventListener('pointerdown', e => {
        if (e.button !== 0) return;
        pointerDragged = false;
        pointerId = e.pointerId;
        startX = e.clientX;
        startY = e.clientY;
      });
      textEl.addEventListener('pointermove', e => {
        if (e.pointerId !== pointerId) return;
        if (dragDistanceExceeded(startX, startY, e.clientX, e.clientY)) {
          pointerDragged = true;
        }
      });
      textEl.addEventListener('pointerup', e => {
        if (e.pointerId !== pointerId) return;
        pointerId = -1;
      });
      textEl.addEventListener('pointercancel', e => {
        if (e.pointerId !== pointerId) return;
        pointerId = -1;
      });
      textEl.addEventListener('click', e => {
        e.stopPropagation();
        const sel = window.getSelection();
        const hasHighlightSelection =
          !coarsePointer &&
          sel &&
          !sel.isCollapsed &&
          textEl.contains(sel.anchorNode);
        if (pointerDragged || hasHighlightSelection) return;
        const si = parseInt(li.dataset['section']!, 10);
        const ii = parseInt(li.dataset['item']!, 10);
        this.openExistingItemEditor(container, sectionsEl, file, si, ii, onDirty);
      });
    });
  }

  wireItemPhotoPickers(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-item-photo').forEach(btn => {
      const si = parseInt(btn.dataset['section']!, 10);
      const ii = parseInt(btn.dataset['item']!, 10);
      const item = file.sections[si]?.items[ii];
      if (!item) return;
      const input = sectionsEl.querySelector<HTMLInputElement>(
        `.item-photo-input[data-section="${si}"][data-item="${ii}"]`
      );
      if (!input) return;

      btn.addEventListener('click', e => {
        e.stopPropagation();
        input.click();
      });
      input.addEventListener('change', () => {
        if (!this.ingestPhotosForItem(item, input)) return;
        this.renderSections(container, file, onDirty);
        onDirty();
      });
    });
  }

  wireItemMediaMenuPickers(
    _container: HTMLElement,
    sectionsEl: HTMLElement,
    _file: DatabaseFile,
    _onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-menu-add-audio, .btn-menu-add-video').forEach(btn => {
      const inputClass = btn.classList.contains('btn-menu-add-audio') ? 'item-audio-input' : 'item-video-input';
      btn.addEventListener('click', e => {
        e.preventDefault();
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const menu = btn.closest('.item-more-menu');
        menu?.querySelector<HTMLInputElement>(`.${inputClass}`)?.click();
      });
    });

    if (sectionsEl.dataset['mediaPickerChangeBound']) return;
    sectionsEl.dataset['mediaPickerChangeBound'] = '1';
    sectionsEl.addEventListener('change', ev => {
      const file = this.state.activeFile;
      const onDirty = this.state.activeOnDirty;
      const container = this.state.activeContainer;
      if (!file || !onDirty || !container) return;

      const input = ev.target;
      if (!(input instanceof HTMLInputElement)) return;
      const isAudio = input.classList.contains('item-audio-input');
      const isVideo = input.classList.contains('item-video-input');
      if (!isAudio && !isVideo) return;

      const si = parseInt(input.dataset['section'] ?? '', 10);
      const ii = parseInt(input.dataset['item'] ?? '', 10);
      const item = file.sections[si]?.items[ii];
      if (!item) return;

      ensureItemMediaFields(item);
      void (async () => {
        if (isAudio) {
          if (!this.ingestAudioForItem(item, input)) return;
          onDirty();
          this.renderSections(container, file, onDirty);
          return;
        }

        const ingest = await this.ingestVideoForItem(item, input);
        if (!ingest.ok) return;

        this.renderSections(container, file, onDirty);
        const failed = await this.capturePendingVideoThumbs(container, item, ingest.filenames);
        if (failed.length === ingest.filenames.length) {
          this.renderSections(container, file, onDirty);
          await appAlert(
            'Could not create a video preview on this device. The clip was not attached.',
            'Video attach failed'
          );
          return;
        }
        if (failed.length > 0) {
          this.renderSections(container, file, onDirty);
          await appAlert(
            `Could not create a preview for: ${failed.join(', ')}. Those clips were not attached.`,
            'Video attach failed'
          );
        }
        onDirty();
        this.renderSections(container, file, onDirty);
      })();
    });
  }

  wireItemListPointerReorder(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLUListElement>('.item-list').forEach(ul => {
      const si = parseInt(ul.dataset['section']!);

      ul.addEventListener('pointerdown', e => {
        if (e.button !== 0) return;
        if (!(e.target as HTMLElement).closest('.item-drag-handle')) return;
        e.stopPropagation();
        this.state.sectionDragGestureAbort?.abort();
        this.state.sectionDragGestureAbort = null;
        const li = (e.target as HTMLElement | null)?.closest('li.item-row') as HTMLLIElement | null;
        if (!li || !ul.contains(li)) return;

        const pid = e.pointerId;
        const el = li;
        const startX = e.clientX;
        const startY = e.clientY;
        let active = false;

        this.state.itemGestureAbort?.abort();
        this.state.itemGestureAbort = new AbortController();
        const g = this.state.itemGestureAbort.signal;

        const finish = (ev: PointerEvent) => {
          if (ev.pointerId !== pid) return;
          if (active) {
            try {
              el.releasePointerCapture(pid);
            } catch {
              /* ignore */
            }
            sectionsEl.classList.remove('item-reorder-active');
            el.classList.remove('item-dragging');
            const items = file.sections[si]!.items;
            const newItems = [...ul.querySelectorAll<HTMLLIElement>('li.item-row')].map(row => {
              const ii = parseInt(row.dataset['item']!);
              return items[ii]!;
            });
            const changed =
              newItems.length !== items.length || newItems.some((it, i) => it !== items[i]);
            if (changed) {
              file.sections[si]!.items = newItems;
              this.renderSections(container, file, onDirty);
              onDirty();
            }
          }
          this.state.itemGestureAbort?.abort();
          this.state.itemGestureAbort = null;
        };

        const onPointerMove = (ev: PointerEvent) => {
          if (ev.pointerId !== pid) return;
          if (!active) {
            if (!dragDistanceExceeded(startX, startY, ev.clientX, ev.clientY)) return;
            active = true;
            sectionsEl.classList.add('item-reorder-active');
            el.classList.add('item-dragging');
            try {
              el.setPointerCapture(pid);
            } catch {
              /* ignore */
            }
          }
          ev.preventDefault();
          const afterEl = getDragAfterElement<HTMLLIElement>(
            ul,
            ev.clientY,
            'li.item-row:not(.item-dragging)'
          );
          if (afterEl == null) ul.appendChild(el);
          else ul.insertBefore(el, afterEl);
        };

        window.addEventListener('pointermove', onPointerMove, { signal: g, passive: false });
        window.addEventListener('pointerup', finish, { signal: g });
        window.addEventListener('pointercancel', finish, { signal: g });
      });
    });
  }

  wireMoveSection(container: HTMLElement, file: DatabaseFile, sectionsEl: HTMLElement): void {
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-move-section').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const section = file.sections[si];
        if (!section) return;

        const dest = await appChoose({
          title: 'Move section',
          message: `Move "${section.title}" to which page?`,
          choices: [
            { value: 'page', label: 'Another page' },
            { value: 'new', label: 'Create a new page' },
          ],
        });
        if (!dest) return;

        btn.disabled = true;
        try {
          if (dest === 'new') {
            const name = await appPrompt('Name for the new page.', {
              title: 'New page',
              inputLabel: 'Page name',
              confirmLabel: 'Create & move',
            });
            if (!name) return;
            await this.onMoveSectionToNew!(file, si, name);
            return;
          }

          const pages = this.listOtherPages!(file.path);
          if (pages.length === 0) {
            await appAlert('There are no other pages to move this section to.');
            return;
          }
          const targetPath = await appChoose({
            title: 'Choose page',
            message: `Move "${section.title}" to which page?`,
            choices: pages.map(p => ({
              value: p.path,
              label: p.name,
              hint: p.path.replace(/^database\//, ''),
            })),
          });
          if (!targetPath) return;
          await this.onMoveSectionToPage!(file, si, targetPath);
        } catch (err) {
          await appAlert(`Move failed: ${(err as Error).message}`, 'Move failed');
        } finally {
          btn.disabled = false;
        }
      });
    });
  }

  wireMoveAllSectionItems(
    container: HTMLElement,
    file: DatabaseFile,
    sectionsEl: HTMLElement
  ): void {
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-move-all-items').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const fromSection = file.sections[si];
        if (!fromSection || fromSection.items.length === 0) {
          await appAlert('This section has no items to move.');
          return;
        }

        const count = fromSection.items.length;
        const sectionTitle = fromSection.title;
        const othersOnPage = sortSectionsByTitle(
          file.sections
            .map((s, sectionIndex) => ({ sectionIndex, title: s.title }))
            .filter(s => s.sectionIndex !== si)
        );

        const pickSectionOnPage = async (): Promise<number | null> => {
          if (othersOnPage.length === 0) return null;
          const picked = await appChoose({
            title: 'Move all items',
            message: `Move ${count} item${count === 1 ? '' : 's'} from "${sectionTitle}" to which section on this page?`,
            choices: [
              ...othersOnPage.map(s => ({
                value: String(s.sectionIndex),
                label: s.title,
              })),
              { value: '__page__', label: 'A section on another page…' },
            ],
          });
          if (picked === null) return null;
          if (picked === '__page__') return -1;
          return parseInt(picked, 10);
        };

        btn.disabled = true;
        try {
          let targetSectionIndex =
            othersOnPage.length > 0 ? await pickSectionOnPage() : -1;
          if (targetSectionIndex === null) return;

          if (targetSectionIndex === -1) {
            const pages = this.listOtherPages!(file.path);
            if (pages.length === 0) {
              await appAlert('There are no other pages to move items to.');
              return;
            }
            const targetPath = await appChoose({
              title: 'Choose page',
              message: `Move all items from "${sectionTitle}" to which page?`,
              choices: pages.map(p => ({
                value: p.path,
                label: p.name,
                hint: p.path.replace(/^database\//, ''),
              })),
            });
            if (!targetPath) return;

            const sections = await this.listPageSections!(targetPath);
            if (sections.length === 0) {
              await appAlert('That page has no sections.');
              return;
            }
            const picked = await appChoose({
              title: 'Choose section',
              message: `Move all items to which section on that page?`,
              choices: sections.map(s => ({
                value: String(s.sectionIndex),
                label: s.title,
              })),
            });
            if (picked === null) return;
            await this.onMoveAllItemsToSection!(
              file,
              si,
              targetPath,
              parseInt(picked, 10)
            );
            return;
          }

          await this.onMoveAllItemsToSection!(file, si, file.path, targetSectionIndex);
        } finally {
          btn.disabled = false;
        }
      });
    });
  }

  wireMoveItem(container: HTMLElement, file: DatabaseFile, sectionsEl: HTMLElement): void {
    sectionsEl.querySelectorAll<HTMLButtonElement>('.btn-move-item').forEach(btn => {
      btn.addEventListener('click', async e => {
        e.stopPropagation();
        this.closeAllMoreMenus(sectionsEl);
        const si = parseInt(btn.dataset['section']!, 10);
        const ii = parseInt(btn.dataset['item']!, 10);
        const item = file.sections[si]?.items[ii];
        if (!item) return;

        const preview = item.text.trim().replace(/\s+/g, ' ').slice(0, 60);
        const label = preview.length < item.text.trim().length ? `${preview}…` : preview;

        const dest = await appChoose({
          title: 'Move item',
          message: `Where should "${label}" go?`,
          choices: [
            { value: 'here', label: 'Another section on this page' },
            { value: 'page', label: 'A section on another page' },
            { value: 'new', label: 'Create a new page' },
          ],
        });
        if (!dest) return;

        btn.disabled = true;
        try {
          if (dest === 'new') {
            const name = await appPrompt('Name for the new page.', {
              title: 'New page',
              inputLabel: 'Page name',
              confirmLabel: 'Create & move',
            });
            if (!name) return;
            await this.onMoveItemToNew!(file, si, ii, name);
            return;
          }

          if (dest === 'here') {
            const sections = sortSectionsByTitle(
              file.sections
                .map((s, sectionIndex) => ({ sectionIndex, title: s.title }))
                .filter(s => s.sectionIndex !== si)
            );
            if (sections.length === 0) {
              await appAlert('This page has no other section to move this item to.');
              return;
            }
            const targetSection = await appChoose({
              title: 'Choose section',
              message: `Move "${label}" to which section on this page?`,
              choices: sections.map(s => ({
                value: String(s.sectionIndex),
                label: s.title,
              })),
            });
            if (targetSection === null) return;
            await this.onMoveItemToSection!(file, si, ii, file.path, parseInt(targetSection, 10));
            return;
          }

          const pages = this.listOtherPages!(file.path);
          if (pages.length === 0) {
            await appAlert('There are no other pages to move this item to.');
            return;
          }
          const targetPath = await appChoose({
            title: 'Choose page',
            message: `Move "${label}" to which page?`,
            choices: pages.map(p => ({
              value: p.path,
              label: p.name,
              hint: p.path.replace(/^database\//, ''),
            })),
          });
          if (!targetPath) return;

          const sections = await this.listPageSections!(targetPath);
          if (sections.length === 0) {
            await appAlert('That page has no sections.');
            return;
          }
          const targetSection = await appChoose({
            title: 'Choose section',
            message: `Move "${label}" to which section?`,
            choices: sections.map(s => ({
              value: String(s.sectionIndex),
              label: s.title,
            })),
          });
          if (targetSection === null) return;
          await this.onMoveItemToSection!(file, si, ii, targetPath, parseInt(targetSection, 10));
        } catch (err) {
          await appAlert(`Move failed: ${(err as Error).message}`, 'Move failed');
        } finally {
          btn.disabled = false;
        }
      });
    });
  }

  renderItem(file: DatabaseFile, item: CheckItem, si: number, ii: number): string {
    const imageGallery = this.renderItemImagesGalleryHtml(file, item);
    const audioGallery = this.renderItemAudioGalleryHtml(file, item);
    const videoGallery = this.renderItemVideoGalleryHtml(file, item);
    const hasMedia = imageGallery.length > 0 || audioGallery.length > 0 || videoGallery.length > 0;
    const formatSpanishQuotes = isSpanishTranslatePage(file.path);
    const textTitle = itemTextTooltipAttr(item, formatSpanishQuotes);
    return `
      <li class="item-row state-${item.state}${hasMedia ? ' item-row-has-media' : ''}" data-section="${si}" data-item="${ii}">
        <div class="item-row-main">
          <button type="button" class="item-drag-handle" aria-label="Drag to reorder" title="Drag to reorder">☰</button>
          <span class="item-text ${item.state === 'done' ? 'done' : ''} ${item.state === 'in-progress' ? 'wip' : ''}"${textTitle}>
            ${renderItemTextHtml(item, formatSpanishQuotes)}
          </span>
          <span class="item-row-actions">
            <button type="button" class="btn-item-photo btn-icon" data-section="${si}" data-item="${ii}" title="Add photos" aria-label="Add photos">📷</button>
            <input type="file" class="item-photo-input" accept="image/*" multiple tabindex="-1" aria-hidden="true" data-section="${si}" data-item="${ii}" />
            <button type="button" class="btn-delete-item btn-icon btn-icon-danger" data-section="${si}" data-item="${ii}" title="Delete item">🗑</button>
            ${this.renderItemMoreMenu(si, ii, file.sections[si]!)}
          </span>
        </div>
        ${imageGallery.length > 0 ? `<div class="item-images" data-section="${si}" data-item="${ii}">${imageGallery}</div>` : ''}
        ${audioGallery.length > 0 ? `<div class="item-audio-list" data-section="${si}" data-item="${ii}">${audioGallery}</div>` : ''}
        ${videoGallery.length > 0 ? `<div class="item-video-list" data-section="${si}" data-item="${ii}">${videoGallery}</div>` : ''}
      </li>`;
  }

  renderItemImagesGalleryHtml(file: DatabaseFile, item: CheckItem): string {
    const tiles: string[] = [];
    for (const filename of item.images) {
      if (item.imagesToDelete.includes(filename)) continue;
      const repoPath = imageRepoPath(file.path, filename);
      tiles.push(`
        <figure class="item-image-tile" data-repo-path="${escapeHtml(repoPath)}" data-filename="${escapeHtml(filename)}" data-pending="0" title="View full size">
          <img class="item-image" alt="" loading="lazy" data-repo-path="${escapeHtml(repoPath)}" />
          <button type="button" class="btn-remove-media" title="Remove photo" aria-label="Remove photo">×</button>
        </figure>`);
    }
    for (const pending of item.pendingImages) {
      const blobUrl = URL.createObjectURL(pending.file);
      this.state.pendingBlobUrls.push(blobUrl);
      tiles.push(`
        <figure class="item-image-tile item-image-tile-pending" data-filename="${escapeHtml(pending.filename)}" data-pending="1" title="View full size">
          <img class="item-image" src="${blobUrl}" alt="Pending upload" />
          <span class="item-media-pending-badge">New</span>
          <button type="button" class="btn-remove-media" title="Remove photo" aria-label="Remove photo">×</button>
        </figure>`);
    }
    return tiles.join('');
  }

  wireMediaDeleteButtons(
    root: HTMLElement,
    _file: DatabaseFile,
    item: CheckItem,
    kind: 'image' | 'audio' | 'video',
    onChange: () => void,
    fileInput?: HTMLInputElement
  ): void {
    root.querySelectorAll<HTMLButtonElement>('.btn-remove-media').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const tile = btn.closest('[data-filename]') as HTMLElement | null;
        if (!tile) return;
        const filename = tile.dataset['filename']!;
        const isPending = tile.dataset['pending'] === '1';
        if (kind === 'image') {
          if (isPending) {
            const wasSaved = item.pendingImages.some(p => p.filename === filename);
            item.pendingImages = item.pendingImages.filter(p => p.filename !== filename);
            if (!wasSaved && fileInput) fileInput.value = '';
          } else if (!item.imagesToDelete.includes(filename)) {
            item.imagesToDelete.push(filename);
          }
        } else if (kind === 'audio') {
          if (isPending) {
            const wasSaved = item.pendingAudio.some(p => p.filename === filename);
            item.pendingAudio = item.pendingAudio.filter(p => p.filename !== filename);
            if (!wasSaved && fileInput) fileInput.value = '';
          } else if (!item.audioToDelete.includes(filename)) {
            item.audioToDelete.push(filename);
          }
        } else if (isPending) {
          const wasSaved = item.pendingVideo.some(p => p.filename === filename);
          item.pendingVideo = item.pendingVideo.filter(p => p.filename !== filename);
          if (!wasSaved && fileInput) fileInput.value = '';
        } else if (!item.videoToDelete.includes(filename)) {
          item.videoToDelete.push(filename);
        }
        onChange();
      });
    });
  }

  wireItemImageActions(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLElement>('.item-images').forEach(gallery => {
      const si = parseInt(gallery.dataset['section']!, 10);
      const ii = parseInt(gallery.dataset['item']!, 10);
      const item = file.sections[si]!.items[ii]!;
      const photoInput = sectionsEl.querySelector<HTMLInputElement>(
        `.item-photo-input[data-section="${si}"][data-item="${ii}"]`
      );
      this.wireMediaDeleteButtons(gallery, file, item, 'image', () => {
        this.renderSections(container, file, onDirty);
        onDirty();
      }, photoInput ?? undefined);
    });
  }

  async hydrateItemImages(container: HTMLElement, generation: number): Promise<void> {
    const imgs = container.querySelectorAll<HTMLImageElement>('img.item-image[data-repo-path]');
    await withoutGitBusyOverlay(() =>
      Promise.all(
        [...imgs].map(async img => {
          const path = img.dataset['repoPath'];
          if (!path || img.src.startsWith('blob:')) return;
          try {
            const url = await this.resolveImageUrl(path, false);
            if (this.state.mediaHydrateGeneration !== generation) return;
            img.src = url;
          } catch {
            if (this.state.mediaHydrateGeneration !== generation) return;
            img.alt = 'Could not load';
            img.classList.add('item-image-error');
          }
        })
      )
    );
  }

  renderItemAudioGalleryHtml(file: DatabaseFile, item: CheckItem): string {
    const tiles: string[] = [];
    for (const filename of item.audio) {
      if (item.audioToDelete.includes(filename)) continue;
      const repoPath = audioRepoPath(file.path, filename);
      const label = escapeHtml(audioDisplayLabel(filename));
      tiles.push(`
        <figure class="item-audio-tile" data-repo-path="${escapeHtml(repoPath)}" data-filename="${escapeHtml(filename)}" data-pending="0" data-media="audio">
          <audio class="item-audio" controls preload="none" data-repo-path="${escapeHtml(repoPath)}"></audio>
          <figcaption class="item-audio-caption">${label}</figcaption>
          <button type="button" class="btn-remove-media" title="Remove audio" aria-label="Remove audio">×</button>
        </figure>`);
    }
    for (const pending of item.pendingAudio) {
      const blobUrl = URL.createObjectURL(pending.file);
      this.state.pendingBlobUrls.push(blobUrl);
      const label = escapeHtml(audioDisplayLabel(pending.filename));
      tiles.push(`
        <figure class="item-audio-tile item-audio-tile-pending" data-filename="${escapeHtml(pending.filename)}" data-pending="1" data-media="audio">
          <audio class="item-audio" controls preload="metadata" src="${escapeAttr(blobUrl)}"></audio>
          <figcaption class="item-audio-caption">${label}</figcaption>
          <span class="item-media-pending-badge">New</span>
          <button type="button" class="btn-remove-media" title="Remove audio" aria-label="Remove audio">×</button>
        </figure>`);
    }
    return tiles.join('');
  }

  wireItemAudioActions(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLElement>('.item-audio-list').forEach(gallery => {
      const si = parseInt(gallery.dataset['section']!, 10);
      const ii = parseInt(gallery.dataset['item']!, 10);
      const item = file.sections[si]!.items[ii]!;
      const audioInput = sectionsEl.querySelector<HTMLInputElement>(
        `.item-audio-input[data-section="${si}"][data-item="${ii}"]`
      );
      this.wireMediaDeleteButtons(gallery, file, item, 'audio', () => {
        this.renderSections(container, file, onDirty);
        onDirty();
      }, audioInput ?? undefined);
    });
  }

  async hydrateItemAudio(container: HTMLElement, generation: number): Promise<void> {
    const clips = container.querySelectorAll<HTMLAudioElement>('audio.item-audio[data-repo-path]');
    await withoutGitBusyOverlay(() =>
      Promise.all(
        [...clips].map(async audio => {
          const path = audio.dataset['repoPath'];
          if (!path || audio.src.startsWith('blob:')) return;
          try {
            const url = await this.resolveAudioUrl(path);
            if (this.state.mediaHydrateGeneration !== generation) return;
            audio.src = url;
            audio.load();
          } catch {
            if (this.state.mediaHydrateGeneration !== generation) return;
            audio.classList.add('item-audio-error');
          }
        })
      )
    );
  }

  private itemVideoTilePlaybackHtml(
    filename: string,
    repoPath: string | null,
    options?: { pendingBlob?: string; thumbPreviewUrl?: string; thumbRepoPath?: string }
  ): string {
    const isAudio = isAudioAttachmentFilename(filename);
    if (isAudio) {
      const blobUrl = options?.pendingBlob;
      const preload = blobUrl ? 'metadata' : 'none';
      const srcAttr = blobUrl ? ` src="${escapeAttr(blobUrl)}"` : '';
      const repoAttr = repoPath ? ` data-repo-path="${escapeHtml(repoPath)}"` : '';
      return `<audio class="item-media-playback item-video item-media-as-audio item-audio" controls preload="${preload}"${repoAttr}${srcAttr}></audio>`;
    }
    const repoAttr = repoPath ? ` data-repo-path="${escapeHtml(repoPath)}"` : '';
    const pendingAttr = options?.pendingBlob ? ` data-pending-blob="${escapeAttr(options.pendingBlob)}"` : '';
    const thumbPathAttr = options?.thumbRepoPath
      ? ` data-thumb-path="${escapeHtml(options.thumbRepoPath)}"`
      : '';
    const thumbImg = options?.thumbPreviewUrl
      ? `<img class="item-video-thumb-img" src="${escapeAttr(options.thumbPreviewUrl)}" alt="" />`
      : `<img class="item-video-thumb-img hidden" alt="" />`;
    const playLabel = escapeHtml(filename);
    return `<button type="button" class="item-video-thumb-play-btn"${repoAttr}${pendingAttr}${thumbPathAttr} aria-label="Play ${playLabel}">
      ${thumbImg}
      <span class="item-video-thumb-icon" aria-hidden="true">▶</span>
    </button>`;
  }

  private showVideoThumbImg(img: HTMLImageElement, src: string, repoPath?: string): void {
    const debugPath =
      repoPath ??
      (img.closest('.item-video-thumb-play-btn') as HTMLButtonElement | null)?.dataset['repoPath'] ??
      '(thumb)';
    img.onerror = () => {
      logThumbDebug(debugPath, 'thumb-img-error', 'Thumbnail image failed to decode', false, {
        srcPrefix: src.slice(0, 80),
      });
    };
    img.onload = () => {
      logThumbDebug(debugPath, 'thumb-img-load', 'Thumbnail decoded', true, {
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight,
      });
    };
    img.src = src;
    img.classList.remove('hidden');
  }

  async hydrateItemVideoThumbs(container: HTMLElement, generation: number): Promise<void> {
    const buttons = [
      ...container.querySelectorAll<HTMLButtonElement>(
        '.item-video-thumb-play-btn[data-repo-path]'
      ),
    ];
    const mdPath = this.state.activeFile?.path;
    const hydrateOne = async (btn: HTMLButtonElement) => {
          const img = btn.querySelector<HTMLImageElement>('.item-video-thumb-img');
          const repoPath = btn.dataset['repoPath'];
          const tile = btn.closest('.item-video-tile');
          if (!img || !repoPath) return;
          if (img.getAttribute('src')) {
            logThumbDebug(repoPath, 'thumb-hydrate-skip', 'Thumbnail already has src');
            return;
          }
          tile?.classList.add('item-video-thumb-loading');
          logThumbDebug(repoPath, 'thumb-hydrate-start', 'Loading gallery thumbnail');

          const thumbPaths = btn.dataset['thumbPath']
            ? videoThumbLoadRepoPaths(repoPath, mdPath)
            : [];
          let thumbSatisfied = false;
          for (const thumbPath of thumbPaths) {
            logThumbDebug(repoPath, 'thumb-repo-try', `Try ${thumbPath.split('/').pop()}`, true, {
              thumbPath,
            });
            try {
              const url = await this.resolveImageUrl(thumbPath);
              if (this.state.mediaHydrateGeneration !== generation) return;
              logThumbDebug(repoPath, 'thumb-repo-ok', 'Loaded committed .thumb.jpg');
              this.showVideoThumbImg(img, url, repoPath);
              try {
                await waitForImageLoad(img);
                if (this.state.mediaHydrateGeneration !== generation) return;
                if (!(await isMostlyBlackImageElement(img))) {
                  thumbSatisfied = true;
                  break;
                }
                logThumbDebug(
                  repoPath,
                  'thumb-repo-black',
                  'Committed .thumb.jpg is mostly black',
                  false
                );
              } catch (err) {
                logThumbDebug(
                  repoPath,
                  'thumb-img-load-fail',
                  err instanceof Error ? err.message : 'thumbnail decode failed',
                  false
                );
              }
              img.classList.add('hidden');
              img.removeAttribute('src');
              break;
            } catch (err) {
              logThumbDebug(
                repoPath,
                'thumb-repo-miss',
                err instanceof Error ? err.message : 'thumb not found',
                false,
                { thumbPath }
              );
            }
          }

          if (!thumbSatisfied) {
            logThumbDebug(
              repoPath,
              'thumb-hydrate-no-repo-thumb',
              'No .thumb.jpg in repo — play icon only (re-attach video and save to upload thumb)',
              false
            );
          }
    };

    await withoutGitBusyOverlay(async () => {
      try {
        if (prefersVideoStreamPlayback()) {
          for (const btn of buttons) await hydrateOne(btn);
        } else {
          await Promise.all(buttons.map(btn => hydrateOne(btn)));
        }
      } finally {
        for (const btn of buttons) {
          btn.closest('.item-video-tile')?.classList.remove('item-video-thumb-loading');
        }
      }
    });
  }

  wireItemVideoLazyPlay(container: HTMLElement, generation: number): void {
    container.querySelectorAll<HTMLButtonElement>('.item-video-thumb-play-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const tile = btn.closest('.item-video-tile');
        if (!tile) return;
        const repoPath = btn.dataset['repoPath'];
        const pendingBlob = btn.dataset['pendingBlob'];
        btn.disabled = true;
        tile.classList.add('item-video-tile-loading');

        const media = document.createElement('video');
        media.className = 'item-media-playback item-video';
        media.setAttribute('controls', '');
        media.setAttribute('playsinline', '');
        media.setAttribute('webkit-playsinline', '');
        media.preload = 'none';

        if (repoPath) {
          media.dataset['repoPath'] = repoPath;
          btn.replaceWith(media);
          void this.hydrateVideoPlaybackElement(media, generation)
            .then(() => {
              void media.play().catch(() => {});
            })
            .finally(() => {
              tile.classList.remove('item-video-tile-loading');
            });
          return;
        }

        if (pendingBlob) {
          media.src = pendingBlob;
          btn.replaceWith(media);
          tile.classList.remove('item-video-tile-loading');
          void media.play().catch(() => {
            media.classList.add('item-video-error');
          });
        }
      });
    });
  }

  private itemVideoCaptionInputHtml(filename: string): string {
    return `<input type="text" class="item-video-caption-input" value="${escapeAttr(filename)}" aria-label="Video filename" spellcheck="false" autocomplete="off" />`;
  }

  renderItemVideoGalleryHtml(file: DatabaseFile, item: CheckItem): string {
    const tiles: string[] = [];
    for (const filename of item.video) {
      if (item.videoToDelete.includes(filename)) continue;
      const repoPath = videoRepoPath(file.path, filename);
      const removeLabel = isAudioAttachmentFilename(filename) ? 'Remove audio' : 'Remove video';
      tiles.push(`
        <figure class="item-video-tile" data-repo-path="${escapeHtml(repoPath)}" data-filename="${escapeHtml(filename)}" data-pending="0" data-media="video">
          ${this.itemVideoTilePlaybackHtml(filename, repoPath, {
            thumbRepoPath: videoThumbnailRepoPath(file.path, filename),
          })}
          ${this.itemVideoCaptionInputHtml(filename)}
          <button type="button" class="btn-remove-media" title="${escapeHtml(removeLabel)}" aria-label="${escapeHtml(removeLabel)}">×</button>
        </figure>`);
    }
    for (const pending of item.pendingVideo) {
      const blobUrl = URL.createObjectURL(pending.file);
      this.state.pendingBlobUrls.push(blobUrl);
      tiles.push(`
        <figure class="item-video-tile item-video-tile-pending" data-filename="${escapeHtml(pending.filename)}" data-pending="1" data-media="video">
          ${this.itemVideoTilePlaybackHtml(pending.filename, null, {
            pendingBlob: blobUrl,
            ...(pending.thumbPreviewUrl ? { thumbPreviewUrl: pending.thumbPreviewUrl } : {}),
          })}
          ${this.itemVideoCaptionInputHtml(pending.filename)}
          <span class="item-media-pending-badge">New</span>
          <button type="button" class="btn-remove-media" title="Remove clip" aria-label="Remove clip">×</button>
        </figure>`);
    }
    return tiles.join('');
  }

  wireVideoFilenameInputs(
    gallery: HTMLElement,
    item: CheckItem,
    onChange: () => void
  ): void {
    gallery.querySelectorAll<HTMLInputElement>('.item-video-caption-input').forEach(input => {
      const tile = input.closest<HTMLElement>('.item-video-tile');
      if (!tile) return;
      const oldName = tile.dataset['filename']!;
      const isPending = tile.dataset['pending'] === '1';

      const stopBubble = (e: Event) => e.stopPropagation();
      input.addEventListener('click', stopBubble);
      input.addEventListener('mousedown', stopBubble);
      input.addEventListener('keydown', e => {
        if (e.key === 'Enter') input.blur();
      });
      input.addEventListener('blur', () => {
        const result = applyVideoFilenameEdit(item, oldName, input.value, isPending);
        if (result === null) {
          void appAlert('Use a unique video filename, for example front-door.mp4');
          input.value = oldName;
          return;
        }
        if (result !== oldName) onChange();
      });
    });
  }

  wireItemVideoActions(
    container: HTMLElement,
    sectionsEl: HTMLElement,
    file: DatabaseFile,
    onDirty: () => void
  ): void {
    sectionsEl.querySelectorAll<HTMLElement>('.item-video-list').forEach(gallery => {
      const si = parseInt(gallery.dataset['section']!, 10);
      const ii = parseInt(gallery.dataset['item']!, 10);
      const item = file.sections[si]!.items[ii]!;
      const videoInput = sectionsEl.querySelector<HTMLInputElement>(
        `.item-video-input[data-section="${si}"][data-item="${ii}"]`
      );
      const refresh = () => {
        this.renderSections(container, file, onDirty);
        onDirty();
      };
      this.wireMediaDeleteButtons(gallery, file, item, 'video', refresh, videoInput ?? undefined);
      this.wireVideoFilenameInputs(gallery, item, refresh);
    });
  }

  async hydrateVideoPlaybackElement(el: HTMLMediaElement, generation: number): Promise<void> {
    await withoutGitBusyOverlay(async () => {
          const path = el.dataset['repoPath'];
          if (!path || el.src.startsWith('blob:')) return;
          const name = path.split('/').pop() ?? '';
          const tile = el.closest('.item-video-tile') as HTMLElement | null;
          const kind = isAudioAttachmentFilename(name) ? 'audio' : 'video';
          if (isMediaDebugEnabled()) {
            logMediaDebug({
              kind,
              phase: 'hydrate-start',
              repoPath: path,
              ok: true,
              message: 'Hydrating committed clip',
            });
          }
          try {
            const url = kind === 'audio'
              ? await this.resolveAudioUrl(path)
              : await this.resolveVideoUrl(path);
            if (this.state.mediaHydrateGeneration !== generation) return;

            const showPlaybackHint = (text: string) => {
              if (!tile) return;
              let hint = tile.querySelector<HTMLElement>('.item-media-error-hint');
              if (!hint) {
                hint = document.createElement('p');
                hint.className = 'item-media-error-hint';
                tile.appendChild(hint);
              }
              hint.textContent = text;
            };

            let playbackFallbackInFlight = false;

            const logPlaybackPhase = (phase: string, message: string, ok = true) => {
              if (!isMediaDebugEnabled()) return;
              logMediaDebug({
                kind,
                phase,
                repoPath: path,
                ok,
                message,
                details: {
                  readyState: el.readyState,
                  networkState: el.networkState,
                  paused: el.paused,
                },
              });
            };

            const showOpenVideoLink = (openUrl: string, label = 'Open video in new tab') => {
              if (!tile) return;
              let link = tile.querySelector<HTMLAnchorElement>('.item-media-open-link');
              if (!link) {
                link = document.createElement('a');
                link.className = 'item-media-open-link';
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                tile.appendChild(link);
              }
              link.href = openUrl;
              link.textContent = label;
            };

            const showTapToPlay = () => {
              if (!tile || kind !== 'video') return;
              let btn = tile.querySelector<HTMLButtonElement>('.item-media-tap-play');
              if (!btn) {
                btn = document.createElement('button');
                btn.type = 'button';
                btn.className = 'item-media-tap-play';
                btn.textContent = 'Tap to play';
                tile.appendChild(btn);
                btn.addEventListener('click', () => {
                  btn?.remove();
                  void el.play().catch(() => {
                    showPlaybackHint('Tap play on the video controls.');
                  });
                });
              }
            };

            const waitForMetadata = (ms: number) =>
              new Promise<void>(resolve => {
                if (el.readyState >= HTMLMediaElement.HAVE_METADATA) {
                  resolve();
                  return;
                }
                const timer = setTimeout(() => resolve(), ms);
                el.addEventListener(
                  'loadedmetadata',
                  () => {
                    clearTimeout(timer);
                    resolve();
                  },
                  { once: true }
                );
              });

            const assignPlaybackSrc = (nextUrl: string) => {
              el.removeAttribute('src');
              el.replaceChildren();
              tile?.querySelector('.item-media-open-link')?.remove();
              tile?.querySelector('.item-media-tap-play')?.remove();
              // crossorigin forces CORS on cross-origin URLs; Firefox Android then stays NETWORK_IDLE.
              el.removeAttribute('crossorigin');
              el.preload = videoPreloadForPlayback();
              const useDirectSrc =
                nextUrl.startsWith('/api/') || prefersFirefoxDirectVideoSrc(nextUrl);
              if (useDirectSrc) {
                el.src = nextUrl;
              } else {
                const source = document.createElement('source');
                source.src = nextUrl;
                source.type = mime;
                el.appendChild(source);
              }
              el.load();
              if (isMediaDebugEnabled()) {
                logPlaybackPhase(
                  'assign-src',
                  useDirectSrc ? `direct src (${nextUrl.slice(0, 48)})` : `source type=${mime}`,
                  true
                );
              }
            };

            let proxyFallbackTimer: ReturnType<typeof setTimeout> | undefined;
            const metadataReady = () =>
              this.state.mediaHydrateGeneration !== generation ||
              el.readyState >= HTMLMediaElement.HAVE_METADATA;

            const tryAssign = async (
              nextUrl: string,
              phase: string,
              message: string
            ): Promise<boolean> => {
              if (this.state.mediaHydrateGeneration !== generation) return true;
              if (el.readyState >= HTMLMediaElement.HAVE_METADATA) return true;
              el.classList.remove('item-video-error');
              tile?.querySelector('.item-media-error-hint')?.remove();
              assignPlaybackSrc(nextUrl);
              logMediaDebug({
                kind,
                phase,
                repoPath: path,
                ok: true,
                message,
                details: { srcPrefix: nextUrl.slice(0, 64) },
              });
              await waitForMetadata(PROXY_PLAYBACK_TIMEOUT_MS);
              return metadataReady();
            };

            const showExhaustedFallbacks = async () => {
              if (isFirefoxBrowser()) {
                const currentSrc = el.currentSrc || el.src;
                const openUrl = currentSrc.startsWith('blob:')
                  ? currentSrc
                  : await this.resolveVideoUrl(path, false, { openInNewTab: true });
                showOpenVideoLink(openUrl);
                showTapToPlay();
                logPlaybackPhase(
                  'playback-exhausted',
                  currentSrc.startsWith('blob:')
                    ? 'Firefox playback exhausted — blob open link + tap to play'
                    : 'Firefox playback exhausted — proxy open link + tap to play',
                  false
                );
              }
            };

            const runPlaybackFallback = async (
              trigger: string,
              fromUrl: string
            ): Promise<void> => {
              if (playbackFallbackInFlight) return;
              if (metadataReady()) return;
              playbackFallbackInFlight = true;
              clearProxyFallback();
              if (isMediaDebugEnabled()) {
                logPlaybackPhase('stream-timeout', trigger, false);
              }
              try {
                if (fromUrl.startsWith('/api/') && !firefoxRejectsCrossOriginVideoSrc()) {
                  const rawUrl = await this.resolveVideoUrl(path, true, {
                    skipProxy: true,
                    rawStreamOnly: true,
                  });
                  if (await tryAssign(
                    rawUrl,
                    'proxy-fallback-raw',
                    'Re-assigned tokenized raw URL after proxy stall'
                  )) {
                    return;
                  }
                }

                const nextUrl = await this.resolveVideoUrl(path, true, {
                  skipProxy: true,
                  skipStream: true,
                });
                if (await tryAssign(
                  nextUrl,
                  'stream-fallback-blob',
                  'Re-assigned blob URL after stream stall'
                )) {
                  return;
                }

                await showExhaustedFallbacks();
              } catch (err) {
                const errMsg = err instanceof Error ? err.message : String(err);
                logPlaybackPhase('stream-fallback-fail', errMsg, false);
              } finally {
                playbackFallbackInFlight = false;
              }
            };

            el.onerror = () => {
              if (this.state.mediaHydrateGeneration !== generation) return;
              const code = el.error?.code;
              const codeLabel =
                code === 1 ? 'aborted'
                : code === 2 ? 'network'
                : code === 3 ? 'decode'
                : code === 4 ? 'src-not-supported'
                : 'unknown';
              const errMsg = `MediaError ${codeLabel} (${code ?? '?'})`;
              if (isMediaDebugEnabled()) {
                logMediaDebug({
                  kind,
                  phase: 'playback-error',
                  repoPath: path,
                  ok: false,
                  message: errMsg,
                  details: {
                    code,
                    networkState: el.networkState,
                    readyState: el.readyState,
                  },
                });
              }
              const currentSrc = el.currentSrc || el.src;
              if (
                code === 4 &&
                firefoxRejectsCrossOriginVideoSrc() &&
                currentSrc.startsWith('http')
              ) {
                void runPlaybackFallback(
                  'MediaError 4 on cross-origin URL — retrying blob',
                  currentSrc
                );
                return;
              }
              el.classList.add('item-video-error');
              showPlaybackHint('Could not play clip — try Save again or re-attach the file.');
            };
            const clearProxyFallback = () => {
              if (proxyFallbackTimer !== undefined) {
                clearTimeout(proxyFallbackTimer);
                proxyFallbackTimer = undefined;
              }
            };

            el.addEventListener(
              'loadedmetadata',
              () => {
                if (this.state.mediaHydrateGeneration !== generation) return;
                clearProxyFallback();
                const probe = getLastMediaProbe(path);
                const dims =
                  kind === 'video'
                    ? `${(el as HTMLVideoElement).videoWidth}x${(el as HTMLVideoElement).videoHeight}`
                    : 'n/a';
                if (isMediaDebugEnabled()) {
                  logMediaDebug({
                    kind,
                    phase: 'loadedmetadata',
                    repoPath: path,
                    ok: true,
                    message: `metadata loaded (${dims}, ${el.duration.toFixed(1)}s)`,
                    ...(probe ? { probe } : {}),
                    details: {
                      videoWidth: (el as HTMLVideoElement).videoWidth,
                      videoHeight: (el as HTMLVideoElement).videoHeight,
                      duration: el.duration,
                      readyState: el.readyState,
                      canPlayMp4: el.canPlayType('video/mp4'),
                      canPlayHevc: el.canPlayType('video/mp4; codecs="hvc1"'),
                    },
                  });
                }
                const note = probe?.warning ?? probe?.issue;
                if (note && tile) showPlaybackHint(note);
              },
              { once: true }
            );

            if (isMediaDebugEnabled()) {
              for (const [evt, phase] of [
                ['stalled', 'stalled'],
                ['suspend', 'suspend'],
                ['waiting', 'waiting'],
                ['canplay', 'canplay'],
              ] as const) {
                el.addEventListener(evt, () => logPlaybackPhase(phase, `${phase} (rs=${el.readyState})`));
              }
            }

            el.classList.remove('item-video-error');
            tile?.querySelector('.item-media-error-hint')?.remove();
            if (kind === 'video') {
              (el as HTMLVideoElement).setAttribute('playsinline', '');
              (el as HTMLVideoElement).setAttribute('webkit-playsinline', '');
            }
            const mime =
              kind === 'audio' ? mimeForAudioPath(path) : mimeForVideoPath(path);
            assignPlaybackSrc(url);
            const needsStallFallback =
              url.startsWith('/api/') ||
              url.startsWith('http') ||
              (url.startsWith('blob:') &&
                (prefersVideoHttpsStreamFirst() || isFirefoxBrowser()));
            if (needsStallFallback) {
              proxyFallbackTimer = setTimeout(() => {
                if (this.state.mediaHydrateGeneration !== generation) return;
                if (el.readyState >= HTMLMediaElement.HAVE_METADATA) return;
                const retryStream = url.startsWith('blob:') && prefersVideoHttpsStreamFirst();
                if (retryStream) {
                  logPlaybackPhase(
                    'blob-timeout',
                    `No metadata after ${PROXY_PLAYBACK_TIMEOUT_MS}ms — retrying HTTPS stream`,
                    false
                  );
                  void (async () => {
                    try {
                      const streamUrl = await this.resolveVideoUrl(path, true, {
                        skipProxy: true,
                        streamOnly: true,
                      });
                      if (await tryAssign(
                        streamUrl,
                        'blob-fallback-stream',
                        'Re-assigned HTTPS stream after blob stall'
                      )) {
                        return;
                      }
                      await showExhaustedFallbacks();
                    } catch (err) {
                      const errMsg = err instanceof Error ? err.message : String(err);
                      logPlaybackPhase('blob-fallback-fail', errMsg, false);
                    }
                  })();
                  return;
                }
                const blobHint = firefoxRejectsCrossOriginVideoSrc()
                  ? 'blob'
                  : url.startsWith('/api/')
                    ? 'PAT raw then blob'
                    : 'blob';
                void runPlaybackFallback(
                  `No metadata after ${PROXY_PLAYBACK_TIMEOUT_MS}ms — retrying ${blobHint}`,
                  url
                );
              }, PROXY_PLAYBACK_TIMEOUT_MS);
            }
            if (isMediaDebugEnabled()) {
              logMediaDebug({
                kind,
                phase: 'hydrate-src',
                repoPath: path,
                ok: true,
                message: url.startsWith('blob:')
                  ? 'Assigned blob URL to media element'
                  : url.startsWith('/api/')
                    ? 'Assigned same-origin proxy URL to media element'
                    : 'Assigned HTTPS stream to media element',
                details: { srcPrefix: url.slice(0, 64), mime },
              });
            }
          } catch (err) {
            if (this.state.mediaHydrateGeneration !== generation) return;
            el.classList.add('item-video-error');
            const errMsg = err instanceof Error ? err.message : 'hydrate failed';
            if (isMediaDebugEnabled()) {
              logMediaDebug({
                kind,
                phase: 'hydrate-fail',
                repoPath: path,
                ok: false,
                message: errMsg,
              });
            }
            if (tile) {
              let hint = tile.querySelector<HTMLElement>('.item-media-error-hint');
              if (!hint) {
                hint = document.createElement('p');
                hint.className = 'item-media-error-hint';
                tile.appendChild(hint);
              }
              hint.textContent = 'Could not load clip — try Save again or re-attach the file.';
            }
          }
    });
  }

  sectionBadge(section: Section): string {
    const open = section.items.filter(i => i.state === 'open').length;
    const wip  = section.items.filter(i => i.state === 'in-progress').length;
    const parts: string[] = [];
    if (open > 0) parts.push(`<span class="badge badge-open">${open}</span>`);
    if (wip  > 0) parts.push(`<span class="badge badge-wip">${wip}</span>`);
    return parts.join('');
  }
}
